import React, { useState, useRef, useEffect } from 'react';
import { LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { normalizeAppearance } from '../../theme/resolveTheme';

const APPEARANCE_OPTIONS = [['classic', 'Classic'], ['light', 'Light'], ['dark', 'Dark'], ['auto', 'Auto']];

// Avatar bubble used in both the web sidebar topbar and the app/tablet topbar —
// click opens a small dropdown with the user's name/role and Sign Out.
export default function ProfileMenu({ user, onLogout, size = 30, showAppearance = true }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { setAppearance } = useAuth();
  const [saving, setSaving] = useState(false);
  const current = normalizeAppearance(user?.appearance);
  // Closes the menu first so a failure toast never covers it; buttons stay disabled while a
  // save is in flight so two quick clicks can't race each other's revert.
  const choose = async (value) => {
    if (value === current || saving) return;
    setOpen(false);
    setSaving(true);
    try { await setAppearance(value); }
    catch { toast.error('Could not save your appearance. Check your connection and try again.'); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="avatar-circle"
        style={{ width: size, height: size, fontSize: size * 0.32, border: 'none', cursor: 'pointer', padding: 0 }}
        title={user?.name}
      >
        {user?.name?.[0]?.toUpperCase()}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 8, minWidth: 240,
          background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 10px 30px rgba(0,0,0,.15)', zIndex: 200, overflow: 'hidden',
        }}>
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: '.88rem' }}>{user?.name}</div>
            <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{user?.role}</div>
          </div>
          {showAppearance && (
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Appearance</div>
            <div role="radiogroup" aria-label="Appearance" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
              {APPEARANCE_OPTIONS.map(([value, label]) => {
                const on = current === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={saving}
                    onClick={() => choose(value)}
                    style={{
                      padding: '5px 0', borderRadius: 6, cursor: saving ? 'wait' : 'pointer', font: 'inherit', fontSize: '.72rem',
                      border: '1px solid var(--border)', fontWeight: on ? 600 : 500,
                      background: on ? 'var(--primary-light)' : 'transparent',
                      color: on ? 'var(--accent-text, var(--primary))' : 'var(--text)',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          )}
          <button
            onClick={onLogout}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
              background: 'none', border: 'none', cursor: 'pointer', fontSize: '.85rem',
              color: 'var(--danger, #dc2626)', textAlign: 'left',
            }}
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
